import { Alert, Box, List, ListItem, ListItemText, Typography, alpha, useTheme } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { ConfirmDialog } from "../../shared/ui";

interface Props {
  open: boolean;
  totalComments: number;
  unresolvedComments: number;
  rejectedSectionLabels: string[];
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Spec §5: prompt the officer "Are you sure you have addressed all comments?" before they
 * resubmit a previously rejected report. Warns when comments are still unresolved but lets
 * the officer override (the spec doesn't require hard-blocking).
 */
export function ResubmitConfirmationDialog({
  open,
  totalComments,
  unresolvedComments,
  rejectedSectionLabels,
  onClose,
  onConfirm,
  loading,
}: Props) {
  const theme = useTheme();
  const hasUnresolved = unresolvedComments > 0;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      tone={hasUnresolved ? "warning" : "primary"}
      icon={<HelpOutlineIcon />}
      maxWidth={480}
      title="Resubmit for CO review?"
      description="Are you sure you have addressed all of the Commanding Officer's comments?"
      confirmLabel="Resubmit"
      loading={loading}
    >
      {rejectedSectionLabels.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.02),
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
          >
            Sections rejected last cycle
          </Typography>
          <List dense disablePadding>
            {rejectedSectionLabels.map((label) => (
              <ListItem disableGutters key={label} sx={{ py: 0.25 }}>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {hasUnresolved ? (
        <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
          You have <strong>{unresolvedComments}</strong> comment
          {unresolvedComments === 1 ? "" : "s"} still marked unresolved (out of{" "}
          {totalComments} total). The CO will see them as still open. You can
          resubmit anyway if you disagree.
        </Alert>
      ) : (
        totalComments > 0 && (
          <Alert severity="success" sx={{ mt: 2, textAlign: "left" }}>
            All {totalComments} comment{totalComments === 1 ? "" : "s"} marked
            resolved.
          </Alert>
        )
      )}
    </ConfirmDialog>
  );
}
